import { ref } from 'vue';
import { $http } from '@/common/api/http.service';
import { useAuthStore } from '@/stores/auth';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { useNotificationStore } from '@/stores/notification';

export function useOutcomeToolsApi() {
    const loading = ref(false);
    const error = ref(null);
    const authStore = useAuthStore();
    const notificationStore = useNotificationStore();

    const getUsers = async (practitioner = null, data = null) => {
        loading.value = true;
        error.value = null;
        try {
            // data:
            // userId
            // loginId
            // level
            // group
            // status

            const practitionerId = practitioner?.id ? `/${practitioner?.id}` : '';
            const response = await $http.get(`/api/account/${authStore.account.id}/outcome-tools/practitioner` + practitionerId, { params: data });
            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error fetching OutcomeTools\u{2120} user.',
                color: 'error',
            });
            error.value = err;
            console.error('Error fetching OutcomeTools\u{2120} user:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const addUser = async (data) => {
        loading.value = true;
        error.value = null;
        try {
            // data:
            // loginId
            // password
            // email
            // phone
            // firstName
            // lastName
            // practitionerId

            const response = await $http.post(`/api/account/${authStore.account.id}/outcome-tools/practitioner`, data);
            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error creating OutcomeTools\u{2120} user.',
                color: 'error',
            });
            error.value = err;
            console.error('Error creating OutcomeTools\u{2120} user:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const updateUser = async (data, practitioner = null) => {
        if (!practitioner && !data.level) {
            notificationStore.add({
                message: 'Error updating OutcomeTools\u{2120} user.',
                color: 'error',
            });
            console.error('Error updating OutcomeTools\u{2120} user:');
            return null;
        } else if (practitioner && !data?.level) {
            // level is required, even when updating. we grab it here because it's not otherwise available in automatic
            // syncing situations
            const {
                data: {
                    data: [outcomeToolsUser],
                },
            } = await getUsers(authStore.practitioner, { userId: authStore.practitioner });
            data.level = outcomeToolsUser.level;
        }

        loading.value = true;
        error.value = null;
        try {
            // data:
            // loginId
            // password
            // email
            // phone
            // firstName
            // lastName
            // practitionerId

            const response = await $http.patch(`/api/account/${authStore.account.id}/outcome-tools/practitioner/${data.practitionerId}`, data);
            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error updating OutcomeTools\u{2120} user.',
                color: 'error',
            });
            error.value = err;
            console.error('Error updating OutcomeTools\u{2120} user:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const addClient = async (practitioner, patient) => {
        loading.value = true;
        error.value = null;
        try {
            const emails = patient?.telecom.filter((com) => com.system === 'email');
            const phones = patient?.telecom.filter((com) => com.system === 'phone');

            const data = {
                firstName: patient.name[0]?.given[0], // required
                lastName: patient.name[0]?.family, // required
                dob: patient?.birthDate ?? 'No DOB specified',
                gender: patient?.gender,
                race: patient?.race?.text,
                email1: emails.pop()?.value,
                email2: emails.pop()?.value,
                phone: phones.pop()?.value,
                address: patient.address[0]?.line[0],
                city: patient?.address[0]?.city,
                state: patient?.address[0]?.state,
                zip: patient?.address[0]?.postalCode,
                country: patient?.address[0]?.country,
                status: 'ACTIVE',
                userId: practitioner.outcomeToolsId,
            };
            // providerId: practitioner.outcomeToolsId, // difference between this and userId? is this more like facility?
            // globalClientId: ,
            // facilityClientId: ,
            // nickName: ,
            // doa':,
            // dod':,
            // emailOptOut: ,
            // method: , // this seems important
            // groups: ,
            // notes: ,

            if (!data.dob) {
                const authStore = useAuthStore();
                notificationStore.add({
                    message: `Error creating OutcomeTools\u{2120} ${authStore.organizationAccount.clientVocab.toLowerCase()}: date of birth is required.`,
                    color: 'error',
                });
                console.error('DOB is required.');
            }

            const response = await $http.post(`/api/account/${authStore.account.id}/outcome-tools/practitioner/${practitioner.id}/client/${patient.id}/create`, data);
            return response.data;
        } catch (err) {
            notificationStore.add({
                message: `Error creating OutcomeTools\u{2120} ${authStore.organizationAccount.clientVocab}.`,
                color: 'error',
            });
            error.value = err;
            console.error('Error creating OutcomeTools\u{2120} client:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const updateClient = async (patient) => {
        loading.value = true;
        error.value = null;
        try {
            const emails = patient.telecom.filter((com) => com.system === 'email');
            const phones = patient.telecom.filter((com) => com.system === 'phone');

            const data = {
                otClientId: patient.outcomeToolsId,
                firstName: patient.name[0]?.given[0], // required
                lastName: patient.name[0]?.family, // required
                dob: patient?.birthDate ?? 'No DOB specified',
                gender: patient?.gender,
                race: patient?.race?.text,
                email1: emails.pop()?.value,
                email2: emails.pop()?.value,
                phone: phones.pop()?.value,
                address: patient.address[0]?.line[0],
                city: patient?.address[0]?.city,
                state: patient?.address[0]?.state,
                zip: patient?.address[0]?.postalCode,
                country: patient?.address[0]?.country,
                status: 'ACTIVE',
                // providerId: ,
                // globalClientId: ,
                // facilityClientId: ,
                // nickName: ,
                // doa':,
                // dod':,
                // emailOptOut: ,
                // method: ,
                // groups: ,
                // notes: ,
            };

            if (!data.dob) {
                const authStore = useAuthStore();
                notificationStore.add({
                    message: `Error updating OutcomeTools\u{2120} ${authStore.organizationAccount.clientVocab.toLowerCase()}: date of birth is required.`,
                    color: 'error',
                });
                console.error('DOB is required.');
            }

            const response = await $http.patch(`/api/account/${authStore.account.id}/outcome-tools/patient/${patient.id}`, data);
            return response.data;
        } catch (err) {
            notificationStore.add({
                message: `Error updating OutcomeTools\u{2120} ${authStore.organizationAccount.clientVocab}.`,
                color: 'error',
            });
            error.value = err;
            console.error('Error updating OutcomeTools\u{2120} client:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const checkPractitionerHasOutcomeTools = async (practitionerId) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.get(`/api/account/${authStore.account.id}/outcome-tools/practitioner/${practitionerId}/has-outcome-tools`);

            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error checking OutcomeTools\u{2120} user.',
                color: 'error',
            });
            error.value = err;
            console.error('Error checking OutcomeTools\u{2120} user:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const checkPatientHasOutcomeTools = async (patientId) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.get(`/api/account/${authStore.account.id}/outcome-tools/client/${patientId}/has-outcome-tools`);

            return response.data;
        } catch (err) {
            const authStore = useAuthStore();
            error.value = err;
            notificationStore.add({
                message: `Error checking OutcomeTools\u{2120} ${authStore.organizationAccount.clientVocab}.`,
                color: 'error',
            });
            console.error('Error checking OutcomeTools\u{2120} client:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const getRelated = async (patient) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await $http.get(`/api/account/${authStore.account.id}/outcome-tools/client/${patient.id}/related`);

            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error getting OutcomeTools\u{2120} related contacts.',
                color: 'error',
            });
            error.value = err;
            console.error('Error getting OutcomeTools\u{2120} related contacts:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const addRelated = async (patientResource, relatedResource) => {
        loading.value = true;
        error.value = null;
        try {
            const relationshipResponse = await $httpFhirApi.get(`BN_Relationship?_include=related,subject&subject=${patientResource.id}&related=${relatedResource.id}`);

            const patient = relationshipResponse.data.filter((item) => ['Patient', 'Practitioner'].includes(item.resourceType) && item.id === patientResource.id)?.[0];
            const related = relationshipResponse.data.filter((item) => ['Patient', 'Practitioner'].includes(item.resourceType) && item.id === relatedResource.id)?.[0];
            const relationship = relationshipResponse.data.filter((item) => item.resourceType === 'BN_Relationship')?.[0];

            // short cicruit - we're usually just dealing with references, we'll rely on checking for the outcomeToolsId here
            if (!patient.outcomeToolsId) return;

            if (!relationship) {
                notificationStore.add({
                    message: 'Could not find relationship record.',
                    color: 'error',
                });
                console.error('Could not find relationship record.');
                return false;
            } else if (!patient.outcomeToolsId) {
                // we'll silently exit here, since we often rely on this function for this check
                return;
            } else if (!patient) {
                notificationStore.add({
                    message: 'Could not find patient record.',
                    color: 'error',
                });
                console.error('Could not find patient record.');
                return false;
            } else if (!related) {
                notificationStore.add({
                    message: 'Could not find contact record.',
                    color: 'error',
                });
                console.error('Could not find contact record.');
                return false;
            }

            const emails = patient?.telecom?.filter((com) => com.system === 'email');
            const phones = patient?.telecom?.filter((com) => com.system === 'phone');
            const relationshipString = relationship.relationship
                .map((item) => item.display)
                .filter((i) => !!i)
                .join(', ');

            const data = {
                otClientId: patient?.outcomeToolsId,
                firstName: related?.name[0]?.given[0],
                lastName: related?.name[0]?.family,
                relationship: relationshipString,
                email1: emails.pop()?.value,
                email2: emails.pop()?.value,
                phone: phones.pop()?.value,
                address: related.address[0]?.line[0],
                city: related.address[0]?.city,
                state: related.address[0]?.state,
                zip: related.address[0]?.postalCode,
                country: related.address[0]?.country,
            };

            const response = await $http.post(`/api/account/${authStore.account.id}/outcome-tools/client/${patient.id}/related/${related.id}`, data);

            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error adding OutcomeTools\u{2120} related contact.',
                color: 'error',
            });
            error.value = err;
            console.error('Error adding OutcomeTools\u{2120} related contact:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const updateRelated = async (patientResource, relatedResource) => {
        loading.value = true;
        error.value = null;
        try {
            const relationshipResponse = await $httpFhirApi.get(`BN_Relationship?_include=related,subject&subject=${patientResource.id}&related=${relatedResource.id}`);

            const patient = relationshipResponse.data.filter((item) => ['Patient', 'Practitioner'].includes(item.resourceType) && item.id === patientResource.id)?.[0];
            const related = relationshipResponse.data.filter((item) => ['Patient', 'Practitioner'].includes(item.resourceType) && item.id === relatedResource.id)?.[0];
            const relationship = relationshipResponse.data.filter((item) => item.resourceType === 'BN_Relationship')?.[0];

            if (!relationship) {
                notificationStore.add({
                    message: 'Could not find relationship record.',
                    color: 'error',
                });
                console.error('Could not find relationship record.');
                return false;
            } else if (!relationship.outcomeToolsRelatedId) {
                notificationStore.add({
                    message: 'Relationship is not connected to OutcomeTools.', // TODO: don't like this
                    color: 'error',
                });
                console.error('Relationship is not connected to OutcomeTools.'); // TODO: don't like this
                return false;
            } else if (!patient.outcomeToolsId) {
                // we'll silently exit here, since we often rely on this function for this check
                return;
            } else if (!patient) {
                notificationStore.add({
                    message: 'Could not find patient record.',
                    color: 'error',
                });
                console.error('Could not find patient record.');
                return false;
            } else if (!related) {
                notificationStore.add({
                    message: 'Could not find contact record.',
                    color: 'error',
                });
                console.error('Could not find contact record.');
                return false;
            }

            const emails = patient?.telecom?.filter((com) => com.system === 'email');
            const phones = patient?.telecom?.filter((com) => com.system === 'phone');
            const relationshipString = relationship.relationship
                .map((item) => item.display)
                .filter((i) => !!i)
                .join(', ');

            const data = {
                otRelatedId: relationship.outcomeToolsRelatedId,
                otClientId: patient?.outcomeToolsId,
                firstName: related?.name[0]?.given[0],
                lastName: related?.name[0]?.family,
                relationship: relationshipString,
                email1: emails.pop()?.value,
                email2: emails.pop()?.value,
                phone: phones.pop()?.value,
                address: related.address[0]?.line[0],
                city: related.address[0]?.city,
                state: related.address[0]?.state,
                zip: related.address[0]?.postalCode,
                country: related.address[0]?.country,
            };

            const response = await $http.patch(`/api/account/${authStore.account.id}/outcome-tools/client/${patient.id}/related/${related.id}`, data);

            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error updating OutcomeTools\u{2120} related contact.',
                color: 'error',
            });
            error.value = err;
            console.error('Error updating OutcomeTools\u{2120} related contact:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const testFacilityId = async (facilityId) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.post(`/api/account/${authStore.account.id}/outcome-tools/facility/verify`, { outcomeToolsFacilityId: facilityId });

            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Verifying OutcomeTools\u{2120} facility failed.',
                color: 'error',
            });
            error.value = err;
            console.error('Verifying OutcomeTools\u{2120} facility failed:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const getLibrary = async () => {
        loading.value = true;
        error.value = null;

        try {
            const response = await $http.get(`/api/account/${authStore.account.id}/outcome-tools/library`);

            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error getting OutcomeTools\u{2120} library.',
                color: 'error',
            });
            error.value = err;
            console.error('Error getting OutcomeTools\u{2120} library:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const getInstance = async () => {
        loading.value = true;
        error.value = null;

        try {
            const response = await $http.get(`/api/account/${authStore.account.id}/outcome-tools/instance`);

            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error getting OutcomeTools\u{2120} instances.',
                color: 'error',
            });
            error.value = err;
            console.error('Error getting OutcomeTools\u{2120} instances:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const assignQuestionnaire = async (data) => {
        loading.value = true;
        error.value = null;

        data = {
            instrumentId: data.instrumentId,
            instanceId: data.instanceId,
            clientId: data.clientId,
            assignedTo: data.assignedTo,
            assignedToType: data.assignedToType,
        };

        try {
            const response = await $http.post(`/api/account/${authStore.account.id}/outcome-tools/questionnaire-assignment/create`, data);

            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error assigning OutcomeTools\u{2120} questionnaire.',
                color: 'error',
            });
            error.value = err;
            console.error('Error assigning OutcomeTools\u{2120} questionnaire:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const getSessions = async (patientOutcomeToolsClientId, sessionId = null, limit = null) => {
        loading.value = true;
        error.value = null;

        const sessionString = sessionId ? `/${sessionId}` : '';
        try {
            const response = await $http.get(`/api/account/${authStore.account.id}/outcome-tools/client/${patientOutcomeToolsClientId}/session${sessionString}`, { params: { limit } });

            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error getting OutcomeTools\u{2120} sessions.',
                color: 'error',
            });
            error.value = err;
            console.error('Error getting OutcomeTools\u{2120} sessions:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const getClientDeepLink = async (userId, clientId) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await $http.get(`/api/account/${authStore.account.id}/outcome-tools/user/${userId}/client/${clientId}/deep-link`);

            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error getting OutcomeTools\u{2120} client link.',
                color: 'error',
            });
            error.value = err;
            console.error('Error getting OutcomeTools\u{2120} client link:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const getSessionDeepLink = async (userId, clientId, sessionId) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await $http.get(`/api/account/${authStore.account.id}/outcome-tools/user/${userId}/client/${clientId}/session/${sessionId}/deep-link`);

            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error getting OutcomeTools\u{2120} client link.',
                color: 'error',
            });
            error.value = err;
            console.error('Error getting OutcomeTools\u{2120} client link:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const getSessionReview = async (patientOutcomeToolsClientId, sessionId) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await $http.get(`/api/account/${authStore.account.id}/outcome-tools/client/${patientOutcomeToolsClientId}/session/${sessionId}/review`);

            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error getting OutcomeTools\u{2120} survey review.',
                color: 'error',
            });
            error.value = err;
            console.error('Error getting OutcomeTools\u{2120} survey review:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const setSurveyReviewers = async (patientOutcomeToolsClientId, sessionId, reviewers) => {
        loading.value = true;
        error.value = null;

        // reviewers = {
        //     practitionerId: bool
        //     79ae85d7-dfb8-44cc-a82d-7bc6cc3b92bd: true
        // }

        try {
            const response = await $http.post(`/api/account/${authStore.account.id}/outcome-tools/client/${patientOutcomeToolsClientId}/session/${sessionId}/reviewers`, { reviewers });

            return response.data;
        } catch (err) {
            notificationStore.add({
                message: 'Error setting survey reviewers.',
                color: 'error',
            });
            error.value = err;
            console.error('Error setting survey reviewers:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    return {
        loading,
        error,
        getUsers,
        addUser,
        updateUser,
        addClient,
        updateClient,
        checkPractitionerHasOutcomeTools,
        checkPatientHasOutcomeTools,
        getRelated,
        addRelated,
        updateRelated,
        testFacilityId,
        getLibrary,
        getInstance,
        assignQuestionnaire,
        getSessions,
        getClientDeepLink,
        getSessionDeepLink,
        getSessionReview,
        setSurveyReviewers,
    };
}
